import Routes from '@/utils/routes';
import type { OccurrenceKind, TourDefinition, TourStep } from './tourTypes';

/**
 * O uso guiado de abrir ocorrência, sobre a plataforma real e os dados reais do
 * parceiro.
 *
 * Substitui o laboratório simulado que existia dentro da prova. O simulado perdia a
 * formatação da tela de verdade e ensinava uma plataforma ligeiramente diferente da
 * que a pessoa usa — o que é pior do que não ensinar, porque cria confiança falsa.
 *
 * Sobre o tom dos textos: quem lê isto usa a plataforma poucas vezes por mês e tem
 * medo de clicar errado e causar prejuízo. Então cada passo diz, nesta ordem, o que
 * a pessoa está vendo, o que fazer agora, e só depois o porquê. Nada de sigla sem
 * tradução, e sempre que uma ação for reversível vale dizer que é — o medo trava
 * mais gente do que a falta de informação.
 *
 * Fonte do conteúdo: MANUAL_ABERTURA_OCORRENCIAS.pdf p5–p15.
 */

const JANELA: Record<OccurrenceKind, string> = {
	NOVA: 'Você tem do 15º ao 45º dia depois do vencimento do boleto para abrir. Antes do 15º ainda é cedo; depois do 45º o sistema não aceita mais.',
	RECORRENTE:
		'Como este contrato já teve ocorrência antes, o prazo é bem mais curto: do 1º ao 15º dia depois do vencimento. Pode abrir já no dia seguinte — e no 16º não dá mais.',
	FINAL: 'Você tem até 30 dias depois da entrega das chaves. Aqui a contagem começa na entrega das chaves, não no vencimento do boleto.',
};

const JANELA_RESUMO: Record<OccurrenceKind, string> = {
	NOVA: 'Primeira cobrança deste contrato. Prazo: do 15º ao 45º dia após o vencimento.',
	RECORRENTE: 'O contrato já teve ocorrência. Prazo: do 1º ao 15º dia após o vencimento.',
	FINAL: 'O inquilino já devolveu as chaves. Prazo: até 30 dias após a entrega.',
};

const DOCUMENTOS: Record<OccurrenceKind, string> = {
	NOVA: 'Você vai precisar de: contrato de locação assinado, os boletos vencidos (com a linha digitável visível), o comprovante de cada conta que você lançou, o log das assinaturas e a vistoria de entrada.',
	RECORRENTE:
		'Você vai precisar de: contrato de locação, os boletos vencidos (com a linha digitável visível) e o comprovante de cada conta que você lançou.',
	FINAL: 'Você vai precisar de: boletos da rescisão, Termo de Entrega das Chaves, vistoria de entrada e de saída, e a 2ª via das contas de consumo. Se houver dano no imóvel, junte também o laudo assinado com fotos coloridas e 3 orçamentos.',
};

const DOCUMENTOS_DICA: Record<OccurrenceKind, string> = {
	NOVA: 'A vistoria de entrada é a que mais faz ocorrência voltar pendente. Confira se ela está aí antes de seguir.',
	RECORRENTE:
		'Cada conta que você lançou no passo anterior precisa do comprovante dela aqui. Faltando um, a ocorrência inteira fica parada.',
	FINAL: 'As fotos do dano precisam estar coloridas dentro do PDF. Foto em preto e branco costuma voltar pendente.',
};

/**
 * @param options.simulated Tutorial com roteiro de treino: nada sai desta máquina, e
 * por isso o passo final muda de sentido — deixa de pedir para parar e passa a pedir
 * para ir até o fim. Praticar a etapa que dá medo é o objetivo do tutorial.
 */
export function buildOccurrenceTour(
	kind: OccurrenceKind,
	options?: { simulated?: boolean },
): TourDefinition {
	const simulated = !!options?.simulated;

	const steps: TourStep[] = [
		{
			id: 'janela',
			title: 'Primeiro: ainda dá tempo de abrir?',
			body: JANELA[kind],
			hint: 'Confira a data antes de começar. Se o prazo já passou, o sistema recusa — e colocar outra data não resolve, porque ele compara com o boleto e reprova sozinho.',
			advance: 'manual',
		},
		{
			id: 'ir-contratos',
			route: { name: Routes.Contracts },
			title: 'Vamos até os seus contratos',
			body: 'Toda ocorrência nasce dentro de um contrato, então o caminho começa por lá. Já estou te levando — é só acompanhar.',
			advance: 'manual',
		},
		{
			id: 'lista',
			target: 'contracts-table',
			ready: { target: 'contracts-table' },
			title: 'Esta é a sua carteira',
			body: 'Cada linha aqui é um contrato seu. A coluna de status mostra em que pé ele está — e só contrato vigente aceita abrir ocorrência.',
			hint: 'Se o contrato que você procura não aparece nesta lista, ele não está na sua carteira. Nesse caso, fale com a Almada antes de continuar.',
			advance: 'manual',
		},
		simulated
			? {
					// No tutorial a lista vem do roteiro e não muda, então pedir para
					// pesquisar seria teatro: a pessoa digitaria, clicaria e nada
					// aconteceria — exatamente o tipo de coisa que destrói a confiança no
					// treinamento. Aqui o passo só mostra onde é.
					id: 'busca',
					target: 'contracts-search',
					title: 'É aqui que você acha o contrato',
					body: 'No dia a dia, você digita neste campo o nome do inquilino ou o CPF dele e clica em "Buscar". Agora não precisa: já deixei o contrato do treino na lista.',
					hint: 'Duas coisas que costumam atrapalhar na hora real: o CPF vai só com números, sem ponto nem traço; e o nome precisa ser o nome completo, como está no contrato.',
					advance: 'manual',
				}
			: {
					id: 'busca',
					target: 'contracts-search',
					title: 'Agora ache o contrato',
					body: 'Digite aqui o nome do inquilino ou o CPF dele e clique em "Buscar". A lista vai mostrar só o contrato dele.',
					// A busca compara o texto cru com o que está gravado, sem tirar pontuação.
					// Digitar "529.982.247-25" não acha nada, e é o erro nº 1 de quem usa pouco.
					hint: 'Duas coisas que costumam atrapalhar: o CPF tem que ir só com números, sem ponto nem traço; e o nome precisa ser o nome completo, do jeito que está no contrato.',
					advance: 'action',
					// Digitar não basta: sem submeter, a lista continua mostrando a carteira
					// inteira e o passo seguinte destacaria o "Abrir" do contrato errado.
					actionTarget: 'contracts-search-submit',
					expect: {
						inputFilled: true,
						searchReflectedIn: { input: 'contracts-search', results: 'contracts-table' },
						message:
							'Ainda não vejo o resultado da busca. Digite o nome completo ou o CPF (só números) e clique em "Buscar". Se a lista voltar vazia, confira se digitou como está no contrato.',
					},
				},
		{
			id: 'abrir-contrato',
			target: 'open-contract-button',
			ready: { target: 'open-contract-button' },
			title: 'Abra o contrato',
			body: 'Achou? Clique no botão "Abrir", no fim da linha, à direita. Lá dentro você vê o contrato inteiro: inquilino, imóvel, valores e os documentos já enviados.',
			hint: 'Pode entrar sem medo: aqui você só está olhando, nada é criado nem alterado.',
			advance: 'action',
			expect: {
				route: Routes.CaseView,
				message:
					'Você ainda está na lista de contratos. Clique no botão "Abrir" destacado, no fim da linha — é ele que leva para dentro do contrato.',
			},
		},
		{
			id: 'aba-ocorrencias',
			target: 'case-tab-ocorrencias',
			ready: { route: Routes.CaseView, target: 'case-tab-ocorrencias' },
			title: 'Clique na aba "Ocorrências"',
			body: 'É aqui que ficam todas as cobranças já abertas neste contrato, e o que aconteceu com cada uma.',
			hint: 'Vale dar uma olhada antes: se já existe ocorrência neste contrato, a próxima é Recorrente — e aí o prazo muda de 45 para 15 dias.',
			advance: 'action',
			expect: {
				target: 'new-occurrence-button',
				message:
					'A aba "Ocorrências" ainda não está aberta. Clique nela, ali no destaque, para eu seguir.',
			},
		},
		{
			id: 'nova-ocorrencia',
			target: 'new-occurrence-button',
			ready: { target: 'new-occurrence-button' },
			// Acima e à esquerda: abaixo do botão é justamente onde o assistente vai
			// abrir, e o balão taparia o que a pessoa precisa ver em seguida.
			placement: 'top-start',
			title: 'Este é o botão que abre a ocorrência',
			body: 'Clique nele. Vai abrir um assistente que te leva por 4 etapas, uma de cada vez: tipo e limites, valores, comprovantes e revisão.',
			hint: 'Nada é enviado enquanto você não chegar na revisão, no fim. Até lá dá para voltar e corrigir à vontade.',
			advance: 'action',
			expect: {
				target: 'occurrence-wizard',
				message:
					'O assistente ainda não abriu. Clique no botão destacado — se ele estiver apagado, é porque este contrato não aceita ocorrência agora.',
			},
		},
		{
			id: 'limites',
			target: 'occurrence-wizard',
			// Cada passo do assistente so aparece na etapa dele. Sem isso, o guia falava
			// de comprovantes enquanto a pessoa ainda estava lancando valores.
			ready: { target: 'occurrence-wizard-step-1' },
			title: 'Etapa 1 de 4 — Tipo: quanto dá para cobrar',
			body: 'A tela mostra o prazo que vale para o seu caso e, do lado direito, quanto ainda dá para cobrar neste contrato. O LMI é o Limite Máximo Indenizatório: o teto total do contrato. O Limite Final é um teto menor, só para as despesas de saída.',
			hint: 'Uma ocorrência só pode cobrar várias coisas de uma vez — aluguel, condomínio, água, luz. Não precisa abrir uma para cada conta.',
			advance: 'manual',
		},
		{
			id: 'contas',
			target: 'occurrence-add-item',
			ready: { target: 'occurrence-wizard-step-2' },
			title: 'Etapa 2 de 4 — Valores: lance o que está em aberto',
			body: 'Clique em "Clique para adicionar" e preencha a categoria, o valor e o vencimento. Repita para cada conta em aberto: pode adicionar quantas linhas precisar, e todas entram na mesma ocorrência.',
			hint: 'O prazo é conferido conta por conta. Pode acontecer de a mais antiga já estar fora e a mais recente ainda estar dentro — e tudo bem, cada uma é avaliada sozinha.',
			advance: 'manual',
		},
		{
			id: 'documentos',
			target: 'occurrence-documents',
			ready: { target: 'occurrence-wizard-step-3' },
			title: 'Etapa 3 de 4 — Comprovantes: anexe os documentos',
			body: DOCUMENTOS[kind],
			hint: `Pode arrastar vários arquivos de uma vez, e cada um recebe o seu tipo na lista ao lado. ${DOCUMENTOS_DICA[kind]}`,
			advance: 'manual',
		},
		{
			id: 'resumo',
			target: 'occurrence-wizard',
			ready: { target: 'occurrence-wizard-step-4' },
			title: 'Etapa 4 de 4 — Revisão: confira com calma',
			body: 'Dê uma última olhada nas categorias e nos valores. Este é o momento de corrigir: depois de criar, não dá mais para incluir nem tirar nada.',
			hint: 'Achou algo errado? Use o "Voltar" do próprio assistente e ajuste. Não custa nada.',
			advance: 'manual',
		},
		simulated
			? {
					id: 'criar',
					target: 'occurrence-wizard',
					ready: { target: 'occurrence-wizard-step-4' },
					title: 'Agora clique em criar — é treino',
					body: 'Clique no botão que cria a ocorrência. Aqui nada é enviado para a Almada e nenhum sinistro é aberto — é justamente esta etapa que você veio praticar.',
					hint: 'No dia real, este clique é o que não tem volta. Faça agora sem medo, para reconhecer a tela quando importar.',
					advance: 'manual',
				}
			: {
					id: 'criar',
					target: 'occurrence-wizard',
					ready: { target: 'occurrence-wizard-step-4' },
					title: 'Criar de verdade',
					body: 'Clicar no botão que cria a ocorrência abre um sinistro real, que entra na fila de análise da Almada com prazo de até 5 dias úteis.',
					warning:
						'Se você está só treinando, pare por aqui. Feche o assistente e nada será criado. Se o caso é real e a documentação está completa, pode seguir tranquilo e criar.',
					pointOfNoReturn: true,
					advance: 'manual',
				},
		{
			id: 'depois',
			title: simulated ? 'Você conseguiu!' : 'Pronto! E agora, o que acontece?',
			body: simulated
				? 'Esse é o caminho inteiro, do começo ao fim. No dia em que for de verdade, é exatamente assim: a ocorrência ganha um número próprio e você acompanha pela aba "Observações", que é por onde a Almada avisa se faltou algo.'
				: 'A ocorrência ganha um número próprio. Acompanhe pela aba "Observações": é por ali que a Almada te avisa se faltou alguma coisa, e é por ali que você responde. Tudo chega também no e-mail cadastrado.',
			hint: 'Se voltar pendente, não é reprovação. Resolva o que foi apontado, anexe o que faltou e clique em REANÁLISE — o analista é avisado na hora.',
			advance: 'manual',
		},
	];

	return {
		id: `occurrence-${kind.toLowerCase()}`,
		title: `Abrir ocorrência ${kind.toLowerCase()}`,
		summary: JANELA_RESUMO[kind],
		steps,
	};
}
